import { finalize } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SearchService } from '../../services/search/search.service';
import { SharedService } from '../../services/shared/shared.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

interface BrowseSearch {
  label: string;
  value: string;
}

interface QueryParameters {
  s: string;
  iso?: string;
  cso?: string;
  i: string;
  ps: string;
}

export interface BrowseSearchResult {
  data: BrowseSearchData;
  message: string;
  status: string;
}

export interface BrowseSearchData {
  browseTerms: BrowseTerm[];
  currentSetId: string;
  nextSetId: string;
  prevSetId: string;
}

export interface BrowseTerm {
  term: string;
  hitCount: number;
  index: string;
  resource: number;
  scopeId: number;
  searchTerm: string;
  title: string;
}

export interface Paging {
  offset: string;
  paging: boolean;
}

enum Browse {
  SEARCH_TERM = 'searchTerm',
  INDEX = 'index',
  PAGE_SIZE = 'pageSize',
  CURRENT_SEARCH_OFFSET = 'currentSearchOffset',
  INITIAL_SEARCH_OFFSET = 'initialSearchOffset',
}

enum BrowseQueryParams {
  SEARCH_TERM = 'st',
  INDEX = 'i',
  PAGE_SIZE = 'ps',
  CURRENT_SEARCH_OFFSET = 'cso',
  INITIAL_SEARCH_OFFSET = 'iso',
}

interface QueryParams {
  st: string; // search term
  i: string; // index
  ps: number; // page size
  cso?: string; // current search offset
  iso?: string; // initial search offset
}

@Component({
  templateUrl: './browse.component.html',
  styleUrls: ['./browse.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BrowseComponent implements OnInit {
  browseForm: UntypedFormGroup;
  pageSizes: number[] = [25, 50, 75, 100];
  indexes: BrowseSearch[] = [{ label: 'Item Call Number', value: 'local_call_number' }];
  browseSearchData: BrowseSearchData;
  browseTerms: BrowseTerm[];
  isLoading: boolean;
  initialSearchOffset: string;

  private _pageSize = this.pageSizes[0];
  private _browseIndex = this.indexes[0].value;

  constructor(
    private _fb: UntypedFormBuilder,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _searchService: SearchService,
    private _sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    this.createForm();
    this._activatedRoute.queryParams.subscribe((params) => {
      if (!params[BrowseQueryParams.SEARCH_TERM]) {
        this.browseTerms = null;
        return;
      }

      this.setFormValues(params);
      this._pageSize = this.browseForm.get(Browse.PAGE_SIZE).value;
      this.initialSearchOffset = params[BrowseQueryParams.INITIAL_SEARCH_OFFSET];

      params[BrowseQueryParams.CURRENT_SEARCH_OFFSET]
        ? this.paging(params[BrowseQueryParams.CURRENT_SEARCH_OFFSET])
        : this.browseSearch();
    });
  }

  private createForm(): void {
    this.browseForm = this._fb.group({
      searchTerm: ['', Validators.required],
      index: [this.indexes[0].value],
      pageSize: this._pageSize,
    });
  }

  private setFormValues(params: Params) {
    this.browseForm.get(Browse.SEARCH_TERM).setValue(params[BrowseQueryParams.SEARCH_TERM]);
    this.browseForm.get(Browse.INDEX).setValue(params[BrowseQueryParams.INDEX]);
    this.browseForm.get(Browse.PAGE_SIZE).setValue(parseInt(params[BrowseQueryParams.PAGE_SIZE]));
  }

  browseSearch(): void {
    if (!this.browseForm.get(Browse.SEARCH_TERM)?.value) {
      return;
    }

    this._pageSize = this.browseForm.get(Browse.PAGE_SIZE)?.value;
    this.toggleLoading(true);
    this._searchService
      .getBrowseSearchResults(
        this.browseForm.get(Browse.INDEX)?.value,
        this.browseForm.get(Browse.SEARCH_TERM)?.value,
        this.browseForm.get(Browse.PAGE_SIZE)?.value,
      )
      .pipe(finalize(() => this.toggleLoading(false)))
      .subscribe(
        (result: BrowseSearchData) => this.successHandler(result),
        (error: unknown) => {
          this.errorHandler(error);
        },
      );
  }

  private paging(offset) {
    this.toggleLoading(true);
    this._searchService
      .getBrowseSearchOffset(this._browseIndex, offset, this._pageSize)
      .pipe(finalize(() => this.toggleLoading(false)))
      .subscribe(
        (result: BrowseSearchData) => this.successHandler(result),
        (error: unknown) => {
          this.errorHandler(error);
        },
      );
  }

  private successHandler(result) {
    this.mapResults(result);
  }

  private errorHandler(error: unknown) {
    console.error(error);
    this._sharedService.unableToCompleteRequestMessage();
  }

  toggleLoading(toggleLoading: boolean): void {
    this.isLoading = toggleLoading;
  }

  private mapResults(result: BrowseSearchData): void {
    if (!this.initialSearchOffset) {
      this.initialSearchOffset = result.currentSetId;
    }

    this.browseSearchData = result;
    this.browseTerms = this.removeNonASCII(result.browseTerms);
  }

  private removeNonASCII(browseTerms: BrowseTerm[]): BrowseTerm[] {
    for (const browseTerm of browseTerms) {
      const splitBrowseTerm = browseTerm.term.split('\x1f');
      browseTerm.searchTerm = splitBrowseTerm[0];
      browseTerm.title = splitBrowseTerm[1];
    }

    return browseTerms;
  }

  setQueryParams(nextOffset: string = null): void {
    this._router.navigate([], { queryParams: this.mapQueryParams(nextOffset) });
  }

  private mapQueryParams(nextOffset: string): QueryParams {
    const queryParams: QueryParams = {
      [BrowseQueryParams.SEARCH_TERM]: this.browseForm.get(Browse.SEARCH_TERM)?.value,
      [BrowseQueryParams.INDEX]: this.browseForm.get(Browse.INDEX)?.value,
      [BrowseQueryParams.PAGE_SIZE]: this.browseForm.get(Browse.PAGE_SIZE)?.value,
    };

    if (nextOffset) {
      queryParams.iso = this.initialSearchOffset;
      queryParams.cso = nextOffset;
      queryParams.ps = this._pageSize;
    }

    return queryParams;
  }
}
